import type { Params } from '@angular/router';

export type PatientSwitchOutcome = 'preserve' | 'remap' | 'fallback';

export interface PatientSwitchRouteResult {
  outcome: PatientSwitchOutcome;
  commands: (string | number)[];
}

interface RouteRemapRule {
  pattern: RegExp;
  listRoute: string;
}

// Detail pages whose entity IDs are patient-specific: land on the parent list for the new patient
const DETAIL_ROUTE_RULES: RouteRemapRule[] = [
  { pattern: /^results\/\d+$/, listRoute: 'results' },
  { pattern: /^report-viewer\//, listRoute: 'reports' },
  { pattern: /^messages\/\d+\/message-view-only$/, listRoute: 'messages' },
  { pattern: /^messages\/\d+\/message-reply$/, listRoute: 'messages' },
  { pattern: /^health-concerns\/\d+$/, listRoute: 'health-concerns' },
  // PBI 57299: Hide Screenings for the August release. Restore these rules with the routes.
  // Screenings drill-downs: histories (template/concept) and record/reminder details are all scoped to
  // the current patient, so a switch lands on the Screenings list for the new patient. record/:id rules
  // precede the history rules so a 4-segment record URL never matches the 3-segment history pattern.
  // { pattern: /^screenings\/questionnaires\/record\/\d+$/, listRoute: 'screenings' },
  // { pattern: /^screenings\/questionnaires\/[^/]+$/, listRoute: 'screenings' },
  // { pattern: /^screenings\/procedures\/record\/\d+$/, listRoute: 'screenings' },
  // { pattern: /^screenings\/procedures\/[^/]+$/, listRoute: 'screenings' },
  // { pattern: /^screenings\/reminders\/\d+$/, listRoute: 'screenings' },
  { pattern: /^goals\/\d+$/, listRoute: 'goals' },
  { pattern: /^medications\/\d+$/, listRoute: 'medications' },
  { pattern: /^letters\/\d+$/, listRoute: 'letters' },
  { pattern: /^letter-detail\/\d+$/, listRoute: 'letters' },
];

// Multi-step flows: switching mid-flow is invalid for the new patient — restart from the list
const FLOW_ROUTE_RULES: RouteRemapRule[] = [
  { pattern: /^messages\/provider$/, listRoute: 'messages' },
  // providerUserId is a Guid (not numeric), so match any non-slash segment, not \d+.
  { pattern: /^messages\/[^/]+\/topic-selection$/, listRoute: 'messages' },
  { pattern: /^messages\/[^/]+\/[^/]+\/new-message$/, listRoute: 'messages' },
  { pattern: /^appointments\/schedule\//, listRoute: 'appointments' },
  { pattern: /^medication-refills\//, listRoute: 'medications' },
  { pattern: /^medication\//, listRoute: 'medications' },
  { pattern: /^patient-pharmacies\//, listRoute: 'medications' },
  { pattern: /^pharmacy-search\//, listRoute: 'medications' },
  { pattern: /^pharmacy-search-results\//, listRoute: 'medications' },
];

// Stable list/overview routes valid for any patient — preserve as-is with the new patientId.
//
// notification-settings and unsubscribe stay in this set even though their content is user-scoped
// rather than patient-scoped: the routes are physically nested under patient/:patientId, so the
// segment must still be remapped on a switch for the URL to remain valid. Keeping the user in place
// is now the better behaviour precisely because the content does not change.
const LIST_ROUTES = new Set([
  '',
  'health-summary',
  'family-history',
  'allergies',
  'problems',
  'health-concerns',
  'surgical-history',
  'immunizations',
  // PBI 57299: Hide Screenings for the August release. Restore with the routes.
  // 'screenings',
  'medical-record',
  'goals',
  'messages',
  'results',
  'reports',
  'appointments',
  'medications',
  'billing',
  'letters',
  'notification-settings',
  'personal-information',
  'feedback',
  'unsubscribe',
]);

// Routes intentionally excluded from LIST_ROUTES fall through to 'fallback' (patient home):
//   edit-profile      — an in-progress edit form; silently re-pointing it at a different patient is
//                       unsafe, so a switch drops to the patient home rather than preserving the form.
//   proxy-access-form — same reasoning, and stronger: the whole form is typed by hand, and its
//                       MRN / last-5 SSN are validated against the *selected* patient's record so
//                       the authorization can't be pointed at the signer's own chart. Switching
//                       patients silently swaps that comparison target, so entries already
//                       accepted could become rejected (or vice versa) under a signature that has
//                       already been given. This exclusion is load-bearing — do not add it below.

/**
 * Path portion of a router URL: leading slash removed, query (?…) and fragment (#…) stripped
 * regardless of order so neither leaks into a match.
 */
function toPath(url: string): string {
  const normalized = (url ?? '').startsWith('/') ? url.slice(1) : (url ?? '');
  return normalized.split('?')[0].split('#')[0];
}

/**
 * Determines the navigation target after a patient switch.
 *
 * @param currentUrl - Current router URL (with or without leading slash; query string and fragment are stripped).
 * @param targetPatientId - The patient being switched to.
 * @returns outcome + Angular router commands for the post-switch navigation.
 *
 * - preserve: same sub-route, new patientId (list/overview pages)
 * - remap:    parent list of the current detail/flow page (entity IDs don't apply to the new patient)
 * - fallback: patient home when the current route isn't patient-scoped or is unrecognised
 */
export function resolvePatientSwitchRoute(currentUrl: string, targetPatientId: number): PatientSwitchRouteResult {
  const patientMatch = toPath(currentUrl).match(/^patient\/\d+\/?(.*)$/);

  if (!patientMatch) {
    return { outcome: 'fallback', commands: ['patient', targetPatientId] };
  }

  const subRoute = patientMatch[1] ?? '';

  for (const rule of DETAIL_ROUTE_RULES) {
    if (rule.pattern.test(subRoute)) {
      return { outcome: 'remap', commands: ['patient', targetPatientId, rule.listRoute] };
    }
  }

  for (const rule of FLOW_ROUTE_RULES) {
    if (rule.pattern.test(subRoute)) {
      return { outcome: 'remap', commands: ['patient', targetPatientId, rule.listRoute] };
    }
  }

  if (LIST_ROUTES.has(subRoute)) {
    const commands: (string | number)[] = ['patient', targetPatientId];
    if (subRoute) {
      commands.push(...subRoute.split('/'));
    }
    return { outcome: 'preserve', commands };
  }

  return { outcome: 'fallback', commands: ['patient', targetPatientId] };
}

// Query params that identify a specific entity owned by the *current* patient (a letter, a goal).
// On a `preserve` switch they must not carry onto the new patient's URL — the entity belongs to the
// previous patient (e.g. patient/1/letters?letterId=555 → patient/2/letters?letterId=555 would point
// at patient 1's letter). Components null their own selection on switch, but stripping the URL here
// keeps it correct on its own and stays robust against any future move to a reactive queryParamMap.
export const ENTITY_SCOPED_QUERY_PARAMS: readonly string[] = ['letterId', 'goalId'];

/**
 * Returns a copy of `queryParams` with the entity-scoped params (see ENTITY_SCOPED_QUERY_PARAMS)
 * removed, for re-attaching the remaining query string to a preserved patient-switch route.
 */
export function stripEntityScopedQueryParams(queryParams: Params): Params {
  const stripped: Params = {};
  for (const [key, value] of Object.entries(queryParams)) {
    if (!ENTITY_SCOPED_QUERY_PARAMS.includes(key)) {
      stripped[key] = value;
    }
  }
  return stripped;
}
